import { SharedAngular } from '@Client/@types/sharedAngular';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import IProcedureSummary from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureSummary';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import {
  IProcedureDeletedMessage,
  IProcedurePublishedMessage,
  IProcedureUpdatedMessage,
  PubSubEvent
} from '@Shared.Angular/@types/pubSub';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import angular from 'angular';

export default class ProceduresService {
  public static $inject = [
    'procedureApiService',
    '$q',
    'pubsubService',
    'APP_CONFIG',
    'permissionsService',
    'tokenService'
  ];

  public static readonly PROCEDURE_RECORDING_START_EVENT =
    'FlowinglyProcedureActivate';
  public selectedCategoryId: Guid = EmptyGuid;

  private procedures: IProcedureSummary[] = null;
  private proceduresPromise: angular.IPromise<IProcedureSummary[]>;

  private isProcedureRecordingExtensionInstalled = false;
  public get IsProcedureRecordingExtensionInstalled() {
    return this.isProcedureRecordingExtensionInstalled;
  }

  constructor(
    private procedureApiService: SharedAngular.ProcedureApiService,
    private $q: angular.IQService,
    private pubsubService: SharedAngular.PubSubService,
    private APP_CONFIG: SharedAngular.APP_CONFIG,
    private permissionsService: SharedAngular.PermissionsService,
    private tokenService: SharedAngular.TokenService
  ) {
    this.proceduresPromise = this.$q.resolve(this.procedures);
    const subscriberId = 'proceduresService';

    this.pubsubService.subscribe(
      'PROCEDURE_UPDATED',
      (event: PubSubEvent, message: IProcedureUpdatedMessage) => {
        const updatedProcedure = message.overview;
        const procedure = this.procedures?.find(
          (p) => p.id === updatedProcedure.id
        );
        if (!procedure) return;

        procedure.name = updatedProcedure.name;
        procedure.description = updatedProcedure.description;
      },
      subscriberId
    );
    this.pubsubService.subscribe(
      'PROCEDURE_DELETED',
      (event: PubSubEvent, message: IProcedureDeletedMessage) => {
        this.procedures = this.procedures?.filter(
          (procedure) => procedure.id !== message.procedureId
        );
        this.proceduresPromise = this.$q.resolve(this.procedures);
      },
      subscriberId
    );
    this.pubsubService.subscribe(
      'PROCEDURE_PUBLISHED',
      (event: PubSubEvent, message: IProcedurePublishedMessage) => {
        if (!this.procedures) {
          return;
        }
        const existingIndex = this.procedures.findIndex(
          (p) => p.id === message.summary.id
        );
        if (existingIndex === -1) {
          this.procedures.push(message.summary);
        } else {
          this.procedures[existingIndex] = message.summary;
        }
        this.proceduresPromise = this.$q.resolve(this.procedures);
      },
      subscriberId
    );

    window.addEventListener('FlowinglyProcedurePingAck', () => {
      this.isProcedureRecordingExtensionInstalled = true;
    });
    const event = new CustomEvent('FlowinglyProcedurePing');
    window.dispatchEvent(event);
  }

  /** Get Procedures published to the current user from the API */
  public getRefreshedProcedures() {
    this.proceduresPromise = this.procedureApiService
      .getProceduresPublishedToCurrentUser()
      .then((response) => {
        this.procedures = response;
        return this.procedures;
      });
    return this.proceduresPromise;
  }

  /** Return cached Procedures published to the current user if any, otherwise get from the API */
  public getProcedures() {
    return this.proceduresPromise.then((procedures) => {
      if (procedures && procedures.length > 0) {
        return procedures;
      }
      return this.getRefreshedProcedures();
    });
  }

  public canRecordProcedure(isMobile: boolean) {
    return (
      !isMobile &&
      this.APP_CONFIG.enableProcedures &&
      this.isProcedureRecordingExtensionInstalled &&
      this.permissionsService.currentUserHasPermission(
        FlowinglyPermissions.PROCEDURE_CREATE
      )
    );
  }

  public startExtensionProcedureRecording(
    procedureId: Guid,
    procedureName: string,
    aiEnhance: boolean
  ) {
    const procedureUrlPostfix =
      '?viewMode=' +
      ProcedureViewMode.Edit +
      (aiEnhance ? '&aiEnhance=true' : '');
    const event = new CustomEvent(
      ProceduresService.PROCEDURE_RECORDING_START_EVENT,
      {
        detail: {
          flowinglyUserToken: this.tokenService.getToken(),
          procedureName: procedureName,
          procedureId: procedureId,
          publicApiUrl: (window.location.origin + '/public/').replace(
            'localhost:9000',
            'localhost:49212'
          ),
          procedureUrl: window.location.origin + '/sops/',
          procedureUrlPostfix,
          maximumActions: 100
        }
      }
    );
    window.dispatchEvent(event);
  }
}

angular
  .module('flowingly.runner.services')
  .service('proceduresService', ProceduresService);

export type ProceduresServiceType = InstanceType<typeof ProceduresService>;
